import { getLogger } from "loglevel";
import type { AddVoiceParticipantOptions, AddVoiceParticipantResponse } from "~/modules/cbm";
import { ParticipantType, CbmImpl } from "~/modules/cbm";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { Action } from "~/modules/actions/Action";
import { LoggerName } from "~/modules/logger";
import { AnalyticsImpl } from "~/modules/analytics/AnalyticsImpl";

import { FlexSdkError, ErrorCode, ErrorSeverity } from "~/modules/error";
import { getReservationsByTask, sendTrackingEvent, getDefaultCallerID } from "~/modules/actions/ActionUtils";
import { AccountConfigDataContainer } from "~/modules/config/AccountConfig/AccountConfigImpl/AccountConfigDataContainer/AccountConfigDataContainer";
import { EVENTS } from "~/modules/analytics/Analytics";






















































export class AddParticipant implements Action<Promise<AddVoiceParticipantResponse>> {
    


    #taskSid: string;

    


    #phoneNumber: string;

    


    #callerId?: string;

    


    #options?: AddVoiceParticipantOptions;

    constructor(taskSid: string, phoneNumber: string, callerId?: string, options?: AddVoiceParticipantOptions) {
        this.#taskSid = taskSid;
        this.#phoneNumber = phoneNumber;
        this.#callerId = callerId;
        this.#options = options;
    }

    async run(ctx: {}): Promise<AddVoiceParticipantResponse> {
        const context = ctx as ContextManager;
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const logger = getLogger(LoggerName.Actions);
        const worker = taskRouter.worker;
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();
        const accountConfig = context.retrieveInstanceOf(
            ContextInstance.AccountConfigDataContainer,
            AccountConfigDataContainer
        );

        const participantType = this.#options?.type || ParticipantType.External;

        logger.debug(`AddParticipant invoked with #taskSid: ${this.#taskSid} and #phoneNumber: ${this.#phoneNumber}`);

        if (!worker) {
            const errorMsg = "AddParticipant: worker is not initialized";
            logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const reservations = getReservationsByTask(worker, this.#taskSid);
        const reservation = reservations.find((res) => res.status === "accepted");

        if (!this.#phoneNumber) {
            const errorMsg = `AddParticipant: #phoneNumber not found`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.AddParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                participantType
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (!reservation) {
            const errorMsg = `AddParticipant: Reservation for task ${this.#taskSid} is not found`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.AddParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                participantType
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const callerIdParam = this.#callerId || getDefaultCallerID(accountConfig.get().outboundCallFlows);
        if (!callerIdParam) {
            const errorMsg = `AddParticipant: #callerId not found`;
            logger.error(errorMsg);
            sendTrackingEvent(EVENTS.AddParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                participantType
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        try {
            const addVoiceParticipantResponse = await cbm.addVoiceParticipant(reservation.task, {
                from: callerIdParam,
                to: this.#phoneNumber,
                type: participantType,
                ...this.#options
            });
            sendTrackingEvent(EVENTS.AddParticipantCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                participantType
            });
            return Promise.resolve(addVoiceParticipantResponse);
        } catch (error) {
            const errorMsg = `AddParticipant: Could not add participant to task: ${error.message}`;
            sendTrackingEvent(EVENTS.AddParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                participantType
            });
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }
    }
}
